<template>
  <v-row no-gutters align="center" class="filter">
    <v-col cols="12">
      <v-row no-gutters align="center" justify="end" class="filter-row">
        <!-- Always rendered: country intersects every scope, including a team,
             superteam or individual (see src/store/actions.js). -->
        <v-col cols="auto" class="filter-col country-col">
          <v-autocomplete
            v-model="country"
            :items="countries"
            :menu-props="{
              bottom: true,
              'nudge-top': -5,
              'offset-y': true,
              'z-index': 10,
            }"
            placeholder="Search Country..."
            hide-details
            single-line
            @change="onCountryChange"
            class="country-select dropdown-select"
          >
            <template #prepend-inner>
              <v-icon :color="countryActive ? 'primary' : ''">place</v-icon>
            </template>
            <template #append>
              <v-icon
                v-if="countryActive"
                color="primary"
                @mousedown.native.stop.prevent
                @click.native.stop="clearCountry"
              >
                close
              </v-icon>
              <v-icon v-else>arrow_drop_down</v-icon>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col
          ref="timeCol"
          cols="auto"
          class="filter-col time-col"
          :class="{ 'time-col--custom': customDateRangeSelected }"
        >
          <!-- v-select (not v-autocomplete): the options are a fixed short list,
               so free-text filtering only adds a confusing text caret.
               The date-picker menu is anchored to this same dropdown: we bind
               only `attrs` (not `on`) so the menu uses the select as its
               position anchor without hijacking clicks — clicking the select
               still opens its preset list; the picker opens programmatically
               when "Custom" is chosen (see onTimeSelectionChange). -->
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            :return-value.sync="dates"
            transition="scale-transition"
            offset-y
            left
            min-width="auto"
            :max-width="290"
          >
            <template v-slot:activator="{ attrs }">
              <v-select
                v-model="selectedTime"
                :items="timeSelections"
                :menu-props="{
                  bottom: true,
                  'nudge-top': -5,
                  'offset-y': true,
                  'z-index': 10,
                }"
                label="Select"
                hide-details
                single-line
                v-bind="attrs"
                @change="onTimeSelectionChange"
                class="dropdown-select time-select"
              >
                <!-- Custom item click reopens the picker even when "Custom" is
                     already selected: @change won't fire without a value change,
                     so we handle the click directly here. -->
                <template #item="{ item, on, attrs }">
                  <v-list-item
                    v-bind="attrs"
                    v-on="on"
                    @click="onTimeItemClick(item)"
                  >
                    <v-list-item-title>{{ item }}</v-list-item-title>
                  </v-list-item>
                </template>
                <template #selection="{ item }">
                  <span
                    v-if="item === 'Custom' && tempDates[0] && tempDates[1]"
                    ref="rangeText"
                    class="time-select__range"
                  >
                    {{ customRangeLabel }}
                  </span>
                  <span v-else>{{ item }}</span>
                </template>
                <template #prepend-inner>
                  <v-icon :color="timeActive ? 'primary' : ''">event</v-icon>
                </template>
                <template #append>
                  <v-icon
                    v-if="timeActive"
                    color="primary"
                    @mousedown.native.stop.prevent
                    @click.native.stop="clearTime"
                  >
                    close
                  </v-icon>
                  <v-icon v-else>arrow_drop_down</v-icon>
                </template>
              </v-select>
            </template>
            <v-date-picker
              ref="datePicker"
              color="primary"
              v-model="dates"
              range
              no-title
              scrollable
              :show-current="true"
              :allowed-dates="allowedDates"
              @click:year="handleYearClick"
              @input="handleDateInput"
              type="date"
            >
              <v-spacer></v-spacer>
              <v-btn class="btn-cancel" text @click="handleCancel">
                Cancel
              </v-btn>
              <v-btn
                class="btn-ok"
                text
                @click="handleApply"
                :disabled="!isValidDateRange"
              >
                Apply
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>
<script>
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
dayjs.extend(isoWeek);
import { mapState, mapActions } from "vuex";

const TIME_SELECTIONS = [
  "All Time",
  "This Week",
  "This Month",
  "This Year",
  "Last Year",
  "Custom",
];

const WORLDWIDE = "Worldwide";

// Named-preset ranges, recomputed against "today" each call. Used to detect
// whether an externally-set range (e.g. restored from a URL query param)
// happens to equal one of these, so the dropdown can show the preset's name
// instead of always falling back to "Custom".
function presetRanges() {
  return {
    "This Week": [
      dayjs().isoWeekday(1).format("YYYY-MM-DD"),
      dayjs().isoWeekday(7).format("YYYY-MM-DD"),
    ],
    "This Month": [
      dayjs().startOf("month").format("YYYY-MM-DD"),
      dayjs().endOf("month").format("YYYY-MM-DD"),
    ],
    "This Year": [
      dayjs().startOf("year").format("YYYY-MM-DD"),
      dayjs().endOf("year").format("YYYY-MM-DD"),
    ],
    "Last Year": [
      dayjs().subtract(1, "year").startOf("year").format("YYYY-MM-DD"),
      dayjs().subtract(1, "year").endOf("year").format("YYYY-MM-DD"),
    ],
  };
}

// The preset name matching [start, end], or null when it's a genuine custom
// range (or doesn't line up with any preset's current bounds).
function matchPreset(start, end) {
  const ranges = presetRanges();
  return (
    Object.keys(ranges).find(
      (name) => ranges[name][0] === start && ranges[name][1] === end
    ) || null
  );
}

// Autofit for the custom-range label inside the time dropdown. As the column
// narrows the label first wraps to two lines (see .time-select__range's
// line-clamp), then shrinks its font from MAX down to MIN before finally
// truncating with an ellipsis. Tune MIN to taste — it's the smallest the range
// text is allowed to get before we let it truncate.
const RANGE_FONT_MAX_PX = 16;
const RANGE_FONT_MIN_PX = 10;
const RANGE_FONT_STEP_PX = 0.5;
const RANGE_MAX_LINES = 2;
// Sub-pixel rounding slack: a label that already fits within the line budget
// shouldn't be shrunk just because its height rounds up a pixel.
const RANGE_FIT_TOLERANCE_PX = 2;

export default {
  data() {
    return {
      dates: [null, null],
      menu: false,
      customDateRangeSelected: false,
      country: WORLDWIDE,
      timeSelections: TIME_SELECTIONS,
      selectedTime: TIME_SELECTIONS[0],
      tempDates: [null, null], // To store temporary dates before applying
    };
  },
  computed: {
    // Label shown inside the time dropdown when a custom range is applied,
    // in place of the word "Custom" (see the v-select #selection slot).
    customRangeLabel() {
      const start = dayjs(this.tempDates[0]).format("YYYY-MM-DD");
      const end = dayjs(this.tempDates[1]).format("YYYY-MM-DD");
      return `${start} ~ ${end}`;
    },
    countries() {
      return [WORLDWIDE].concat(this.countryOptions);
    },
    countryActive() {
      return this.country && this.country !== WORLDWIDE;
    },
    timeActive() {
      return this.selectedTime && this.selectedTime !== TIME_SELECTIONS[0];
    },
    ...mapState({
      countryOptions: "countries",
      storeCountry: "country",
      storeStartDay: "startDay",
      storeEndDay: "endDay",
    }),
    isValidDateRange() {
      return this.dates[0] && this.dates[1];
    },
  },
  watch: {
    // Keep the dropdown in sync when the country filter is set elsewhere (e.g.
    // the map's crosshair button). null (Worldwide) maps back to the sentinel.
    storeCountry(newCountry) {
      const next = newCountry || WORLDWIDE;
      if (this.country !== next) this.country = next;
    },
    // Keep the time dropdown in sync when the date filter changes elsewhere:
    // cleared (e.g. the filter-summary chip's × button) resets back to
    // "All Time"; set (e.g. restored from a URL query param on load) shows
    // the range as "Custom" instead of leaving the dropdown showing a stale
    // "All Time"/preset label.
    storeStartDay(newStartDay) {
      if (!newStartDay) {
        if (this.timeActive) {
          this.selectedTime = TIME_SELECTIONS[0];
          this.customDateRangeSelected = false;
          this.dates = [null, null];
          this.tempDates = [null, null];
        }
        return;
      }
      if (newStartDay !== this.tempDates[0] && this.storeEndDay) {
        this.dates = [newStartDay, this.storeEndDay];
        this.tempDates = [newStartDay, this.storeEndDay];
        const preset = matchPreset(newStartDay, this.storeEndDay);
        this.selectedTime = preset || "Custom";
        this.customDateRangeSelected = !preset;
      }
    },
    // When the picker opens, seed it with the previously applied range so the
    // user edits from where they left off rather than a blank picker.
    menu(isOpen) {
      if (isOpen && this.tempDates[0] && this.tempDates[1]) {
        this.dates = [...this.tempDates];
      }
    },
    // Re-fit the range label whenever it appears or its text changes.
    customRangeLabel() {
      this.$nextTick(this.scheduleFit);
    },
    customDateRangeSelected() {
      this.$nextTick(this.scheduleFit);
    },
  },
  mounted() {
    // The dashboard grid resizes this column without a window resize event, so
    // observe the column directly to keep the range label fitted. The ref may
    // resolve to the VCol component (use its $el) or, depending on Vuetify's
    // functional rendering, the raw DOM node — handle both.
    const ref = this.$refs.timeCol;
    const colEl = ref && (ref.$el || ref);
    if (window.ResizeObserver && colEl instanceof Element) {
      this._rangeObserver = new ResizeObserver(() => this.scheduleFit());
      this._rangeObserver.observe(colEl);
    }
  },
  beforeDestroy() {
    if (this._rangeObserver) this._rangeObserver.disconnect();
  },
  methods: {
    ...mapActions([
      "updateStartDay",
      "updateEndDay",
      "updateCountry",
      "updateStridesUsers",
      "updateStridesCountries",
    ]),
    allowedDates(val) {
      // Allow dates up to today
      return val <= dayjs().format("YYYY-MM-DD");
    },

    // Reopen the date picker when "Custom" is clicked while already selected.
    // A fresh Custom selection is handled by onTimeSelectionChange instead.
    onTimeItemClick(item) {
      if (item === "Custom" && this.customDateRangeSelected) {
        this.$nextTick(() => {
          this.menu = true;
        });
      }
    },

    onTimeSelectionChange(selectedTime) {
      let startDay = null;
      let endDay = null;

      switch (selectedTime) {
        case "All Time":
          this.customDateRangeSelected = false;
          this.dates = [null, null];
          this.tempDates = [null, null];
          break;
        case "This Week":
          this.customDateRangeSelected = false;
          startDay = dayjs().isoWeekday(1).format("YYYY-MM-DD");
          endDay = dayjs().isoWeekday(7).format("YYYY-MM-DD");
          break;
        case "This Month":
          this.customDateRangeSelected = false;
          startDay = dayjs().startOf("month").format("YYYY-MM-DD");
          endDay = dayjs().endOf("month").format("YYYY-MM-DD");
          break;
        case "This Year":
          this.customDateRangeSelected = false;
          startDay = dayjs().startOf("year").format("YYYY-MM-DD");
          endDay = dayjs().endOf("year").format("YYYY-MM-DD");
          break;
        case "Last Year":
          this.customDateRangeSelected = false;
          startDay = dayjs()
            .subtract(1, "year")
            .startOf("year")
            .format("YYYY-MM-DD");
          endDay = dayjs()
            .subtract(1, "year")
            .endOf("year")
            .format("YYYY-MM-DD");
          break;
        case "Custom":
          // Reset dates when switching to Custom
          this.dates = [null, null];
          this.tempDates = [null, null];
          this.customDateRangeSelected = true;
          // Open the date picker
          this.$nextTick(() => {
            this.menu = true;
          });
          return;
      }

      // Update store
      this.updateStartDay(startDay);
      this.updateEndDay(endDay);

      // Update both dates and tempDates to ensure display is consistent
      if (startDay && endDay) {
        this.dates = [startDay, endDay];
        this.tempDates = [startDay, endDay];
      } else {
        this.dates = [null, null];
        this.tempDates = [null, null];
      }
    },

    onCountryChange() {
      const country = this.country === "Worldwide" ? null : this.country;
      this.updateCountry(country);
    },
    clearCountry() {
      this.country = WORLDWIDE;
      this.onCountryChange();
    },
    clearTime() {
      this.selectedTime = TIME_SELECTIONS[0];
      this.onTimeSelectionChange(TIME_SELECTIONS[0]);
    },
    handleYearClick() {
      // Force the picker to stay open after year selection
      this.$nextTick(() => {
        if (this.$refs.datePicker) {
          this.$refs.datePicker.activePicker = "DATE";
        }
      });
    },

    handleDateInput(dates) {
      // Update the dates array when user selects dates
      this.dates = dates;

      // If we have both dates selected, store them temporarily
      if (dates && dates.length === 2 && dates[0] && dates[1]) {
        this.tempDates = [...dates].sort();
      }
    },

    handleCancel() {
      // Just close the popup without restoring previous dates
      this.menu = false;
    },

    handleApply() {
      if (!this.isValidDateRange) return;

      // Ensure dates are in correct order
      const [startDate, endDate] = [...this.dates].sort();

      // Save the dates to be shown in the filter
      this.tempDates = [startDate, endDate];

      // Update the store with the selected date range
      this.updateStartDay(startDate);
      this.updateEndDay(endDate);

      // Set custom as the selected time
      this.selectedTime = "Custom";
      this.customDateRangeSelected = true;

      // Close the picker; the selection slot renders the range reactively.
      this.menu = false;
    },

    // Coalesce the (potentially rapid) resize/watch triggers into one fit per
    // animation frame so we don't thrash layout during a drag-resize.
    scheduleFit() {
      if (this._fitScheduled) return;
      this._fitScheduled = true;
      window.requestAnimationFrame(() => {
        this._fitScheduled = false;
        this.fitRangeText();
      });
    },

    // Shrink the range label's font from MAX toward MIN until it fits within
    // RANGE_MAX_LINES lines; if it still overflows at MIN, the clamp truncates
    // it. Always resets to MAX first so the text grows back when space returns.
    fitRangeText() {
      const el = this.$refs.rangeText;
      if (!el) return;

      // A -webkit-line-clamp box clips its overflow and does NOT report it via
      // scrollHeight, so temporarily lay the label out as a plain block to read
      // the true wrapped height, then restore the CSS-driven clamp.
      el.style.webkitLineClamp = "unset";
      el.style.display = "block";
      el.style.overflow = "visible";

      let size = RANGE_FONT_MAX_PX;
      el.style.fontSize = `${size}px`;

      // line-height is unitless in CSS, so the budget scales with the font.
      const budget = () =>
        parseFloat(getComputedStyle(el).lineHeight) * RANGE_MAX_LINES +
        RANGE_FIT_TOLERANCE_PX;

      while (size > RANGE_FONT_MIN_PX && el.scrollHeight > budget()) {
        size = Math.max(RANGE_FONT_MIN_PX, size - RANGE_FONT_STEP_PX);
        el.style.fontSize = `${size}px`;
      }

      el.style.webkitLineClamp = "";
      el.style.display = "";
      el.style.overflow = "";
    },
  },
};
</script>
<style lang="scss">
@import "@/styles/mixins.scss";
@import "@/styles/variables.scss";

.v-menu__content {
  border-radius: 10px;
}
.v-date-picker-table .v-btn.v-btn--active {
  background: $color-primary-gradient !important;
  color: $color-on-primary !important;
  font-weight: bold;
  border-color: transparent !important;
  &::before {
    background-color: transparent;
  }
}

.v-select-list {
  padding: 0;
}
// Style both the keyboard/hover-highlighted row (--highlighted) and the
// currently-selected row (--active) with the pronounced primary gradient.
// Without the --active rule Vuetify falls back to its default active-tile
// look: it adds a `primary--text` class (lime text) plus a faint primary
// ::before overlay, which reads as flat green-on-muted-green. Neutralise
// that ::before so the gradient shows cleanly.
.v-select-list .v-list-item--highlighted,
.v-select-list .v-list-item--active {
  background: $color-primary-gradient !important;
  &::before {
    opacity: 0;
  }
  .v-list-item__title {
    color: $color-on-primary !important;
    font-weight: bold;
  }
}

/* Add these styles to fix the year picker appearance */
.v-date-picker-years {
  height: 320px !important;
  overflow-y: auto;
}

.v-date-picker-years li {
  cursor: pointer;
  padding: 8px 0;

  &.active {
    color: $color-text-primary !important;
    font-weight: bold;
  }

  &:hover {
    background: $color-primary-gradient !important;
    color: $color-on-primary !important;
  }
}
</style>
<style lang="scss" scoped>
@import "@/styles/mixins.scss";
@import "@/styles/variables.scss";
.btn-cancel {
  text-transform: none;
  color: $color-text-faint;
}
.btn-ok {
  text-transform: none;
}
// Horizontal + vertical gap between filter items (row uses no-gutters).
// Down to the mobile breakpoint the row stays on one line (nowrap) so items
// SHRINK to fit (same as above 800px); only on mobile do they wrap/stack
// vertically. Below 800px the row left-aligns within the strip.
.filter-row {
  gap: 10px 12px;
  flex-wrap: nowrap !important;

  @include below-desktop {
    justify-content: flex-start !important;
  }

  @include mobile {
    flex-wrap: wrap !important;
  }
}
// Let the filter columns shrink from their max width down to a min width
// (Vuetify's col-auto is flex 0 0 auto, which blocks shrinking).
.filter-col {
  flex: 0 1 auto !important;
  min-width: 0;
}
.time-col {
  width: 165px;
  min-width: 60px;
}
// While a custom range is active, base the column on its content width so the
// full ISO range (YYYY-MM-DD ~ YYYY-MM-DD) shows on one line when there's room.
// Keep the shared .filter-col shrink behaviour (flex: 0 1 auto) so it still
// shrinks and truncates with the other filters as the container narrows.
.time-col--custom {
  width: auto;
}
// The #selection slot content is NOT wrapped in Vuetify's .v-select__selection
// (that's replaced by the slot), so it doesn't inherit the truncation rule
// below. As the column shrinks the range wraps, but line-clamp caps it at two
// lines and appends an ellipsis once it would overflow further.
// (min-width: 0 lets this flex child shrink past its content size.)
.time-select__range {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  min-width: 0;
  // Explicit (unitless) line-height so fitRangeText() can compute the N-line
  // budget from getComputedStyle — "normal" is unparseable.
  line-height: 1.3;
}
// The v-select renders its own (invisible, non-searchable) <input>, which has
// flex-grow and so adds empty space to the right once the column sizes to its
// content. Collapse it — the time select is never typed into.
.time-select ::v-deep .v-select__selections input {
  width: 0;
  min-width: 0;
  padding: 0;
}
.country-col {
  width: 200px;
  min-width: 60px;
}
// On mobile every filter stacks full-width, one per row.
@include mobile {
  .time-col,
  .time-col--custom,
  .country-col {
    width: 100%;
    min-width: 0;
  }
}
// The inputs fill their (shrinking) column.
.dropdown-select {
  width: 100%;
}
// Allow shrink to propagate down through the nested rows/cols.
.filter,
.filter ::v-deep .col-12 {
  min-width: 0;
}
.dropdown-select {
  padding-top: 0;
  margin-top: 0;
  align-items: center;
  cursor: pointer !important;

  ::v-deep,
  ::v-deep * {
    cursor: pointer !important;
  }

  ::v-deep {
    .v-input__slot {
      min-height: 42px;
      padding: 0 8px 0 12px;
      border: 1px solid $color-border;
      background-color: $color-raised;
      border-radius: 8px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
      cursor: pointer;
      input,
      .v-icon {
        cursor: pointer;
      }
      // Vuetify tints the focused input's caret with the primary colour; the
      // searchable country dropdown should show a caret matching its text.
      input {
        caret-color: auto;
      }
      &::before,
      &::after {
        display: none;
      }
    }
    label {
      top: 0;
      height: 36px;
      line-height: 36px;
    }
    .v-input__prepend-outer {
      margin-top: 0;
    }
    .v-input__prepend-inner {
      margin-top: 0;
      align-self: center;
    }
    .v-input__append-inner {
      margin-top: 0;
      align-self: center;
    }
  }
}
</style>
